/**
 * Archive Enhancements for Projekt Galerie
 * Features: Random sorting, date sorting, project navigation
 */

class ArchiveManager {
  constructor() {
    this.gridContainer = document.getElementById('project-grid-container');
    this.sortShuffleBtn = document.getElementById('sort-shuffle-btn');
    this.sortDateBtn = document.getElementById('sort-date-btn');
    this.toggleBtn = document.getElementById('archiveToggleBtn');
    this.headerToggleBtn = document.getElementById('toggleBtnHeader'); // Header toggle button
    this.projects = [];
    this.currentProjectIndex = 0;
    this.currentSlideIndex = 0; // Initialize slideshow state
    this.isRandomMode = true; // Start in random mode
    
    this.init();
  }

  init() {
    if (!this.gridContainer) return;
    
    // Get all project components
    this.projects = Array.from(this.gridContainer.querySelectorAll('.project-popup-component'));
    
    // Apply initial sorting based on data attribute
    const section = document.querySelector('.project-gallery-section');
    const sortMode = section?.dataset.sortMode || 'random';
    this.applySorting(sortMode);
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Set up project navigation
    this.setupProjectNavigation();
    
    // Initialize header toggle button state
    this.updateToggleButton(this.isRandomMode);
  }

  setupEventListeners() {
    // Toggle button (primary for archiv page)
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => {
        this.toggleSortMode();
      });
    }

    // Header toggle button (for archive page)
    if (this.headerToggleBtn) {
      this.headerToggleBtn.addEventListener('click', () => {
        this.toggleSortMode();
      });
    }

    // Shuffle button (fallback)
    if (this.sortShuffleBtn) {
      this.sortShuffleBtn.addEventListener('click', () => {
        this.shuffleProjects();
        this.updateButtonStates('shuffle');
        this.updateToggleButton(true); // Update toggle to random mode
      });
    }

    // Date sort button (fallback)
    if (this.sortDateBtn) {
      this.sortDateBtn.addEventListener('click', () => {
        this.sortByDate();
        this.updateButtonStates('date');
        this.updateToggleButton(false); // Update toggle to sorted mode
      });
    }
  }

  applySorting(mode) {
    switch (mode) {
      case 'random':
        this.shuffleProjects();
        this.updateButtonStates('shuffle');
        this.isRandomMode = true;
        break;
      case 'date_desc':
      case 'date_asc':
        this.sortByDate(mode === 'date_asc');
        this.updateButtonStates('date');
        this.isRandomMode = false;
        break;
      case 'title':
        this.sortByTitle();
        this.updateButtonStates('title');
        this.isRandomMode = false;
        break;
      default:
        this.shuffleProjects();
        this.updateButtonStates('shuffle');
        this.isRandomMode = true;
    }
  }

  shuffleProjects() {
    // Fisher-Yates shuffle algorithm
    const shuffled = [...this.projects];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    // Re-append to container in new order
    shuffled.forEach(project => {
      this.gridContainer.appendChild(project);
    });

    // Update projects array and indices
    this.projects = shuffled;
    this.updateProjectIndices();
  }

  sortByDate(ascending = false) {
    const sorted = [...this.projects].sort((a, b) => {
      // Get the actual creation date from Shopify (most reliable)
      const createdAtA = a.dataset.createdAt;
      const createdAtB = b.dataset.createdAt;
      
      // Try to get release date from metafield
      const releaseA = a.dataset.releaseDate;
      const releaseB = b.dataset.releaseDate;
      
      // Try to parse various date formats
      const dateA = this.parseDate(releaseA) || this.parseDate(createdAtA);
      const dateB = this.parseDate(releaseB) || this.parseDate(createdAtB);
      
      // Handle cases where dates couldn't be parsed
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      
      return ascending ? dateA - dateB : dateB - dateA;
    });

    // Re-append to container in new order
    sorted.forEach(project => {
      this.gridContainer.appendChild(project);
    });

    // Update projects array and indices
    this.projects = sorted;
    this.updateProjectIndices();
  }

  parseDate(dateString) {
    if (!dateString) return null;
    
    // Try parsing as ISO string first (from Shopify)
    let date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
    
    // Try common German date formats
    const germanDatePatterns = [
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, // DD.MM.YYYY
      /^(\d{1,2})\.(\d{2})\.(\d{4})$/, // DD.MM.YYYY
      /^(\d{2})\.(\d{2})\.(\d{4})$/, // DD.MM.YYYY
    ];
    
    for (const pattern of germanDatePatterns) {
      const match = dateString.match(pattern);
      if (match) {
        const [, day, month, year] = match;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    // Try month name patterns (German months)
    const monthNames = {
      'januar': 0, 'jan': 0,
      'februar': 1, 'feb': 1,
      'märz': 2, 'mär': 2, 'mar': 2,
      'april': 3, 'apr': 3,
      'mai': 4,
      'juni': 5, 'jun': 5,
      'juli': 6, 'jul': 6,
      'august': 7, 'aug': 7,
      'september': 8, 'sep': 8, 'sept': 8,
      'oktober': 9, 'okt': 9, 'oct': 9,
      'november': 10, 'nov': 10,
      'dezember': 11, 'dez': 11, 'dec': 11
    };
    
    // Try "Month YYYY" or "Month Year" format
    const monthYearPattern = /^(\w+)\s+(\d{4})$/i;
    const monthYearMatch = dateString.toLowerCase().match(monthYearPattern);
    if (monthYearMatch) {
      const [, monthStr, year] = monthYearMatch;
      const month = monthNames[monthStr.toLowerCase()];
      if (month !== undefined) {
        date = new Date(parseInt(year), month, 1);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    // Last resort: try to extract any 4-digit year
    const yearMatch = dateString.match(/(\d{4})/);
    if (yearMatch) {
      date = new Date(parseInt(yearMatch[1]), 0, 1); // January 1st of that year
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    return null;
  }

  sortByTitle() {
    const sorted = [...this.projects].sort((a, b) => {
      const titleA = a.querySelector('.project-popup__dialog-title')?.textContent?.trim() || '';
      const titleB = b.querySelector('.project-popup__dialog-title')?.textContent?.trim() || '';
      return titleA.localeCompare(titleB);
    });

    // Re-append to container in new order
    sorted.forEach(project => {
      this.gridContainer.appendChild(project);
    });

    // Update projects array and indices
    this.projects = sorted;
    this.updateProjectIndices();
  }

  updateProjectIndices() {
    // Update data-product-index for all projects after sorting
    this.projects.forEach((project, index) => {
      project.dataset.productIndex = index;
      const component = project.querySelector('project-popup-component');
      if (component) {
        component.dataset.projectIndex = index;
      }
    });
  }

  toggleSortMode() {
    // Add animation class to grid container
    if (this.gridContainer) {
      this.gridContainer.classList.add('toggle-animating');
    }

    // Add animation class to toggle buttons
    if (this.toggleBtn) {
      this.toggleBtn.classList.add('toggle-animating');
      this.toggleBtn.style.pointerEvents = 'none';
    }
    if (this.headerToggleBtn) {
      this.headerToggleBtn.classList.add('toggle-animating');
      this.headerToggleBtn.style.pointerEvents = 'none';
    }

    // Start the animation sequence
    setTimeout(() => {
      if (this.isRandomMode) {
        // Switch to sorted mode
        this.sortByDate();
        this.isRandomMode = false;
      } else {
        // Switch to random mode
        this.shuffleProjects();
        this.isRandomMode = true;
      }
      
      this.updateToggleButton(this.isRandomMode);
      this.updateButtonStates(this.isRandomMode ? 'shuffle' : 'date');
      
      // End animation after 0.6 seconds total
      setTimeout(() => {
        if (this.gridContainer) {
          this.gridContainer.classList.remove('toggle-animating');
        }
        
        // Re-enable buttons and remove animation classes
        if (this.toggleBtn) {
          this.toggleBtn.classList.remove('toggle-animating');
          this.toggleBtn.style.pointerEvents = '';
        }
        if (this.headerToggleBtn) {
          this.headerToggleBtn.classList.remove('toggle-animating');
          this.headerToggleBtn.style.pointerEvents = '';
        }
      }, 300); // 300ms after sorting = 600ms total
    }, 300); // 300ms delay before sorting
  }

  updateToggleButton(isRandomMode) {
    // Update archive toggle button
    if (this.toggleBtn) {
      const randomSrc = this.toggleBtn.getAttribute('data-random-src');
      const sortedSrc = this.toggleBtn.getAttribute('data-sorted-src');
      
      if (isRandomMode) {
        // Currently in random mode, show sorted button (swapped)
        this.toggleBtn.src = sortedSrc;
      } else {
        // Currently in sorted mode, show random button (swapped)
        this.toggleBtn.src = randomSrc;
      }
    }

    // Update header toggle button
    if (this.headerToggleBtn) {
      const origSrc = this.headerToggleBtn.getAttribute('data-orig-src');
      const altSrc = this.headerToggleBtn.getAttribute('data-alt-src');
      
      if (isRandomMode) {
        // Currently in random mode, show drag button (alternative) - swapped
        this.headerToggleBtn.src = altSrc;
      } else {
        // Currently in sorted mode, show grid button (original) - swapped
        this.headerToggleBtn.src = origSrc;
      }
    }
  }

  updateButtonStates(activeButton) {
    // Reset all buttons
    if (this.sortShuffleBtn) {
      this.sortShuffleBtn.classList.remove('button--primary');
      this.sortShuffleBtn.classList.add('button--secondary');
    }
    if (this.sortDateBtn) {
      this.sortDateBtn.classList.remove('button--primary');
      this.sortDateBtn.classList.add('button--secondary');
    }

    // Highlight active button
    const activeBtn = activeButton === 'shuffle' ? this.sortShuffleBtn : this.sortDateBtn;
    if (activeBtn) {
      activeBtn.classList.remove('button--secondary');
      activeBtn.classList.add('button--primary');
    }
  }

  setupProjectNavigation() {
    // Set up navigation for all project popups
    document.addEventListener('click', (e) => {
      const navButton = e.target.closest('.project-nav-button');
      if (!navButton) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const direction = navButton.dataset.navDirection;
      const currentDialog = navButton.closest('dialog');
      const currentPopup = navButton.closest('.project-popup-component');
      const currentIndex = parseInt(currentPopup.dataset.productIndex);
      
      let nextIndex;
      if (direction === 'next') {
        nextIndex = (currentIndex + 1) % this.projects.length;
      } else {
        nextIndex = (currentIndex - 1 + this.projects.length) % this.projects.length;
      }
      
      // Get next project data
      const nextProject = this.projects[nextIndex];
      this.slideToProject(currentDialog, nextProject, nextIndex, direction);
    });
  }

  slideToProject(dialog, targetProject, targetIndex, direction = 'next') {
    // Get the dialog content container
    const dialogContent = dialog.querySelector('.project-popup__dialog-content');
    if (!dialogContent) return;

    // Get target project data
    const targetComponent = targetProject.querySelector('project-popup-component');
    const targetDialog = targetProject.querySelector('dialog');
    
    if (!targetComponent || !targetDialog) return;

    // Clone the target dialog content
    const targetContent = targetDialog.querySelector('.project-popup__dialog-content');
    const clonedContent = targetContent.cloneNode(true);
    
    // STEP 1: SMART NUCLEAR OPTION - Clear only content, keep close button
    
    // Store the parent container (this is the dialog-content div)
    const parentContainer = dialogContent.parentNode;
    
    // Create an invisible placeholder to maintain dialog structure
    const hiddenPlaceholder = document.createElement('div');
    hiddenPlaceholder.style.cssText = `
      width: 100%;
      height: 100%;
      min-height: 400px;
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
    `;
    hiddenPlaceholder.innerHTML = '<!-- TRANSITION PLACEHOLDER -->';
    
    // Only replace the dialog-content, leave close button intact
    parentContainer.replaceChild(hiddenPlaceholder, dialogContent);
    
    // Also remove ALL text content from cloned content - including navigation
    clonedContent.querySelectorAll('.project-popup__content').forEach(el => el.remove());
    clonedContent.querySelectorAll('.project-popup__dialog-title').forEach(el => el.remove());
    clonedContent.querySelectorAll('.project-popup__description').forEach(el => el.remove());
    clonedContent.querySelectorAll('.project-popup__release-date').forEach(el => el.remove());
    clonedContent.querySelectorAll('.project-popup__project-nav').forEach(el => el.remove());
    
    // Store reference for later restoration
    this._hiddenPlaceholder = hiddenPlaceholder;
    this._originalDialogContent = dialogContent;
    
    // Create transition wrapper for the slide animation
    const transitionWrapper = document.createElement('div');
    transitionWrapper.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
    `;
    
    // Determine slide direction based on navigation direction
    let currentOutTransform, newInTransform, newStartTransform;
    
    if (direction === 'next') {
      // Next: current slides out to left, new slides in from right
      currentOutTransform = 'translateX(-100%)';
      newInTransform = 'translateX(0%)';
      newStartTransform = 'translateX(100%)';
    } else {
      // Previous: current slides out to right, new slides in from left
      currentOutTransform = 'translateX(100%)';
      newInTransform = 'translateX(0%)';
      newStartTransform = 'translateX(-100%)';
    }
    
    // Prepare original content for slide out (only images, no text)
    this._originalDialogContent.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      transform: translateX(0%);
    `;
    
    // Prepare new content for slide in (only images, no text)
    clonedContent.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      transform: ${newStartTransform};
    `;
    
    // Replace hidden placeholder with transition wrapper
    parentContainer.replaceChild(transitionWrapper, this._hiddenPlaceholder);
    transitionWrapper.appendChild(this._originalDialogContent);
    transitionWrapper.appendChild(clonedContent);
    
    // Initialize slideshow component in cloned content if it exists
    const clonedSlideshow = clonedContent.querySelector('slideshow-component');
    if (clonedSlideshow && window.SlideshowComponent) {
      // Re-initialize slideshow component
      setTimeout(() => {
        if (clonedSlideshow.connectedCallback) {
          clonedSlideshow.connectedCallback();
        }
        
        // Re-initialize slideshow event listeners for the new content
        this.reinitializeSlideshowListeners(clonedContent);
      }, 450);
    }
    
    // Start slide animation with direction-specific transforms
    requestAnimationFrame(() => {
      this._originalDialogContent.style.transform = currentOutTransform;
      clonedContent.style.transform = newInTransform;
    });
    
    // STEP 2: HIDE NAVIGATION ONLY IN CURRENT DIALOG - ZERO OVERLAP STRATEGY
    setTimeout(() => {
      // STEP 2A: Hide navigation only in the current transition elements
      const transitionNavs = transitionWrapper.querySelectorAll('.project-popup__project-nav');
      transitionNavs.forEach(nav => {
        nav.style.display = 'none';
        nav.style.visibility = 'hidden';
        nav.style.opacity = '0';
      });
      
      // Clean slate: only slideshow/image remains in cloned content
      const slideshowElement = clonedContent.querySelector('slideshow-component, .project-popup__slideshow, img');
      const slideshowHTML = slideshowElement ? slideshowElement.outerHTML : '';
      clonedContent.innerHTML = slideshowHTML;
      
    }, 400); // Hide navigation during slide
    
    // STEP 3: Add ONLY navigation first - after slide is completely done
    setTimeout(() => {
      // Get fresh content
      const freshTargetDialog = this.projects[targetIndex].querySelector('dialog');
      const freshTargetContent = freshTargetDialog.querySelector('.project-popup__dialog-content');
      const freshNavigation = freshTargetContent.querySelector('.project-popup__project-nav');
      
      if (freshNavigation) {
        const navClone = freshNavigation.cloneNode(true);
        navClone.style.opacity = '0';
        navClone.style.visibility = 'hidden';
        navClone.style.display = 'none';
        navClone.style.transition = 'opacity 0.3s ease';
        
        clonedContent.appendChild(navClone);
        
        // Show navigation with delay
        setTimeout(() => {
          navClone.style.display = 'flex';
          navClone.style.visibility = 'visible';
          requestAnimationFrame(() => {
            navClone.style.opacity = '1';
          });
        }, 100);
      }
    }, 900); // Wait for slide to complete
    
    // STEP 4: Add text content last - after navigation is stable
    setTimeout(() => {
      const freshTargetDialog = this.projects[targetIndex].querySelector('dialog');
      const freshTargetContent = freshTargetDialog.querySelector('.project-popup__dialog-content');
      const freshTextContent = freshTargetContent.querySelector('.project-popup__content');
      
      if (freshTextContent) {
        const textClone = freshTextContent.cloneNode(true);
        textClone.style.opacity = '0';
        textClone.style.transition = 'opacity 0.3s ease';
        textClone.style.display = 'block';
        textClone.style.visibility = 'visible';
        
        clonedContent.appendChild(textClone);
        
        requestAnimationFrame(() => {
          textClone.style.opacity = '1';
        });
      }
    }, 1200); // Wait for navigation to be fully stable
    
    // Clean up after animation - create completely fresh content
    setTimeout(() => {
      // Get fresh target dialog to ensure we have clean, original content
      const freshTargetDialog = this.projects[targetIndex].querySelector('dialog');
      const freshTargetContent = freshTargetDialog.querySelector('.project-popup__dialog-content');
      
      // Create final clean content by cloning fresh original
      const finalContent = freshTargetContent.cloneNode(true);
      finalContent.style.cssText = '';
      
      // Configure the final text element properly
      const finalTextContent = finalContent.querySelector('.project-popup__content');
      if (finalTextContent) {
        finalTextContent.style.display = 'block';
        finalTextContent.style.opacity = '1';
        finalTextContent.style.transition = '';
      }
      
      // Replace transition wrapper with clean final content
      parentContainer.replaceChild(finalContent, transitionWrapper);
      
      // Update the original popup's index for future navigation
      const originalComponent = dialog.closest('.project-popup-component');
      if (originalComponent) {
        originalComponent.dataset.productIndex = targetIndex;
        // Also update any data attributes that might be used for identification
        originalComponent.dataset.productId = targetProject.dataset.productId;
      }
      
      // Re-setup navigation listeners for new content
      this.setupNavigationListenersForDialog(dialog);
      
      // Re-initialize slideshow listeners for the final content
      this.reinitializeSlideshowListeners(finalContent);
      
      // Clean up stored references
      delete this._hiddenPlaceholder;
      delete this._originalDialogContent;
    }, 1100); // Wait for text fade in to complete
  }

  setupNavigationListenersForDialog(dialog) {
    // Remove existing listeners to prevent duplicates
    const navButtons = dialog.querySelectorAll('.project-nav-button');
    navButtons.forEach(button => {
      // Clone node to remove all event listeners
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
    });
    
    // Note: New event listeners are handled by the global setupProjectNavigation method
    // which uses event delegation and will automatically work for new buttons
  }

  reinitializeSlideshowListeners(content) {
    // Find the slideshow component in the content
    const slideshow = content.querySelector('slideshow-component');
    const slides = content.querySelectorAll('slideshow-slide');
    const prevButton = content.querySelector('[data-direction="previous"]');
    const nextButton = content.querySelector('[data-direction="next"]');

    if (slides && slides.length > 1 && prevButton && nextButton) {
      // Initialize slideshow state
      this.currentSlideIndex = 0;
      const totalSlides = slides.length;
      
      // Remove existing event listeners first to prevent duplicates
      prevButton.removeEventListener('click', this.handlePrevious);
      nextButton.removeEventListener('click', this.handleNext);
      
      // Set up the slides container width based on number of slides
      const slidesContainer = content.querySelector('slideshow-slides');
      if (slidesContainer) {
        // Container should be totalSlides * 100% to fit all slides
        slidesContainer.style.width = `${totalSlides * 100}%`;
        
        // Each slide should be 100% of viewport width, which is 100/totalSlides% of container width
        slides.forEach(slide => {
          slide.style.width = `${100 / totalSlides}%`;
          slide.style.flexShrink = '0'; // Prevent slides from shrinking
        });
      }
      
      // Create handler functions
      this.handlePrevious = () => {
        const oldIndex = this.currentSlideIndex;
        this.currentSlideIndex = (this.currentSlideIndex - 1 + totalSlides) % totalSlides;
        console.log(`Previous: ${oldIndex} -> ${this.currentSlideIndex}`);
        this.showSlide(content, this.currentSlideIndex, totalSlides);
        // Remove focus from button to prevent lingering focus outline on mobile
        if (document.activeElement === prevButton) {
          prevButton.blur();
        }
      };
      
      this.handleNext = () => {
        const oldIndex = this.currentSlideIndex;
        this.currentSlideIndex = (this.currentSlideIndex + 1) % totalSlides;
        console.log(`Next: ${oldIndex} -> ${this.currentSlideIndex}`);
        this.showSlide(content, this.currentSlideIndex, totalSlides);
        // Remove focus from button to prevent lingering focus outline on mobile
        if (document.activeElement === nextButton) {
          nextButton.blur();
        }
      };
      
      // Add event listeners
      prevButton.addEventListener('click', this.handlePrevious);
      nextButton.addEventListener('click', this.handleNext);
      
      console.log('Slideshow event listeners re-initialized');
      // Initialize first slide
      this.showSlide(content, 0, totalSlides);
    }
  }

  showSlide(content, index, totalSlides) {
    const slides = content.querySelectorAll('slideshow-slide');
    const slidesContainer = content.querySelector('slideshow-slides');
    
    if (slides && slidesContainer) {
      console.log(`Showing slide ${index} of ${totalSlides} total slides`);
      
      // Each slide takes 100/totalSlides% of container width
      // So we need to move by that percentage for each slide
      const slideWidthPercent = 100 / totalSlides;
      const translateX = -index * slideWidthPercent;
      console.log(`Transform: translateX(${translateX}%) (slide width: ${slideWidthPercent}%)`);
      slidesContainer.style.transform = `translateX(${translateX}%)`;
      
      // Update aria-hidden attributes for accessibility
      slides.forEach((slide, i) => {
        if (i === index) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }
  }
}

// Entrance Animation Management
class ProjectEntranceAnimation {
  constructor() {
    this.hasPlayed = false;
    this.animationDuration = 600; // 0.6s
    this.staggerDelay = 50; // 0.05s between cards
    this.init();
  }

  init() {
    // Check if animation has already played in this session
    if (sessionStorage.getItem('projectEntrancePlayed')) {
      this.hasPlayed = true;
      this.removeEntranceClasses();
      return;
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAnimation());
    } else {
      this.setupAnimation();
    }
  }

  setupAnimation() {
    const cards = document.querySelectorAll('.project-card-entrance');
    
    if (cards.length === 0) return;

    // Calculate total animation duration
    const totalDuration = (cards.length * this.staggerDelay) + this.animationDuration;
    
    // Mark animation as played
    sessionStorage.setItem('projectEntrancePlayed', 'true');
    this.hasPlayed = true;

    // Remove entrance classes after animation completes
    setTimeout(() => {
      this.removeEntranceClasses();
    }, totalDuration);
  }

  removeEntranceClasses() {
    const cards = document.querySelectorAll('.project-card-entrance');
    cards.forEach(card => {
      card.classList.remove('project-card-entrance');
      card.classList.add('animation-complete');
    });
  }

  // Method to reset animation (for testing)
  reset() {
    sessionStorage.removeItem('projectEntrancePlayed');
    this.hasPlayed = false;
    const cards = document.querySelectorAll('.animation-complete');
    cards.forEach(card => {
      card.classList.remove('animation-complete');
      card.classList.add('project-card-entrance');
    });
  }
}

// Initialize entrance animation
const projectEntranceAnimation = new ProjectEntranceAnimation();

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ArchiveManager();
});

// Also initialize if DOM is already loaded (for dynamic loading)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArchiveManager();
  });
} else {
  new ArchiveManager();
} 